import React, { FC, useCallback, useEffect, useMemo } from 'react';
import useRootSelector from 'common/hooks/useRootSelector';
import { List as ImmutableList } from 'immutable';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DictionaryList } from 'common/constants';
import { ImmutableObject } from 'common/state/interfaces';
import { ILeadProfile, ILeadProfileImt, INewLeadProfileData } from 'modules/crm/interfaces/leads';
import { ICameFromIndicatorDictionaryItemImt } from 'common/interfaces/dictionary';
import {
  IImmutablePrimaryMemberInfo,
  IPersonSalesInfoImt,
} from 'common/components/PersonProfile/interfaces';
import { IDictionaryCommonItem } from 'modules/dictionaries/interfaces/interfaces';
import { selectDictionaryList } from 'common/state/dictionary/selectors';
import { fetchDictionaryList } from 'common/state/dictionary/actions';
import * as actions from 'modules/crm/state/leads/actions';
import { fetchLeadActivities } from 'modules/crm/state/leads/actions';
import * as selectors from 'modules/crm/state/leads/selectors';
import { selectUserSelectedClubId } from 'modules/authentication/state/selectors';
import { useAppDispatch } from 'store/hooks';
import { LeadPrimaryInfoStep } from 'modules/crm/components';
import { PeakModules } from 'common/constants/peakModules';
import { useGetSalespersons } from 'api/dictionary.api';

interface IEditLead {
  isOpen?: boolean;
  onClose?: () => void;
}

const EditLead: FC<IEditLead> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const { data: salespersonsPayload } = useGetSalespersons();
  const salespersons = useMemo(() => salespersonsPayload?.data || [], [salespersonsPayload?.data]);
  const leadProfile: ImmutableObject<ILeadProfile> = useRootSelector(selectors.selectLeadForm);

  const leadProfileSalesInfo: IPersonSalesInfoImt = useRootSelector(
    selectors.selectLeadFormSalesInfo,
  );
  const leadActionResult: ILeadProfileImt = useRootSelector(selectors.selectLeadActionResult);

  const cameFromIndicators: ImmutableList<ICameFromIndicatorDictionaryItemImt> = useRootSelector(
    selectDictionaryList(DictionaryList.CAME_FROM),
  );
  const selectedClubId: string = useRootSelector(selectUserSelectedClubId);

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const leadId = Number(id);

  const module =
    location.pathname.indexOf('personal-training-crm') === -1
      ? PeakModules.Crm
      : PeakModules.PersonalTrainingCrm;

  useEffect(() => {
    dispatch(fetchDictionaryList(DictionaryList.CAME_FROM, { module }));
    dispatch(actions.fetchLeadProfile(leadId, module, true));
    dispatch(actions.fetchLeadFormSalesInfo(leadId, module));
    dispatch(fetchLeadActivities(module));
  }, [dispatch, leadId, module]);

  useEffect(() => {
    if (leadActionResult.get('id')) {
      const isPTCRMModule = module === PeakModules.PersonalTrainingCrm;
      const isCRMModule = module === PeakModules.Crm;
      const shouldNavigateToCrm = isCRMModule && leadActionResult.get('leadMember') === false;
      const shouldNavigateToPTCrm = isPTCRMModule && leadActionResult.get('ptLeadMember') === false;

      dispatch(actions.resetLeadProfileActionResult());

      if (shouldNavigateToCrm) {
        navigate('/crm/leads');
      }

      if (shouldNavigateToPTCrm) {
        navigate('/personal-training-crm/leads');
      }

      if (!shouldNavigateToCrm && !shouldNavigateToPTCrm) {
        dispatch(actions.fetchLeadProfile(leadId, module, true));

        if (onClose) {
          onClose();
        }
      }
    }
  }, [dispatch, leadActionResult, onClose, leadId, module, navigate]);

  const handleOnSubmitStep = useCallback(
    (data: Partial<INewLeadProfileData>): void => {
      const transformedActivitiesIds = (data.activitiesIds as IDictionaryCommonItem[]).map(
        activityItem => activityItem.id,
      ) as string[];

      // TODO - PRM-3575 need types
      const payload: any = {
        ...data,
        id: leadProfile.get('id'),
        activitiesIds: transformedActivitiesIds,
        assignedSalespersonId: data.assignedSalesperson?.id || null,
      };

      dispatch(actions.updateLeadProfile(selectedClubId, payload, module));
    },
    [dispatch, leadProfile, selectedClubId, module],
  );

  const handleOnExistingPerson = useCallback(
    (person: IImmutablePrimaryMemberInfo): void => {
      navigate(`/${location.pathname.split('/')[1]}/leads/edit/${person.get('id')}`);
    },
    [location.pathname, navigate],
  );

  return (
    <>
      {!!leadProfile && (
        <LeadPrimaryInfoStep
          module={module}
          cameFromIndicators={cameFromIndicators}
          employees={salespersons}
          primaryInfo={leadProfile}
          salesInfo={leadProfileSalesInfo}
          setPrimaryInfo={handleOnExistingPerson}
          hideAutocomplete
          saveLeadProfile={handleOnSubmitStep}
          isOpen={isOpen}
          onClose={onClose}
        />
      )}
    </>
  );
};

export default EditLead;
